import type {
  OrdersSummary,
  DistanceComparison,
  RoadCostsComparison,
  FuelComparison,
  LoadedKmComparison,
} from "../data/types"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { getIntlLocale } from "@/lib/i18n/format"

interface SummaryTabProps {
  ordersSummary: OrdersSummary
  distanceComparison: DistanceComparison
  roadCostsComparison: RoadCostsComparison
  fuelComparison: FuelComparison
  loadedKmComparison: LoadedKmComparison
}

const formatNumber = (value: number) => {
  return new Intl.NumberFormat(getIntlLocale("pl-PL")).format(value)
}

// Fixed decimals with the decimal comma the Polish report has always used;
// English gets a decimal point.
const formatDecimal = (value: number, digits: number) => {
  const fixed = value.toFixed(digits)
  return getIntlLocale("pl-PL") === "pl-PL" ? fixed.replace(".", ",") : fixed
}

// Orders Summary Card - horizontal 6-column layout
function OrdersSummaryCard({ data }: { data: OrdersSummary }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg border border-[#e5e5e5] bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-xl font-semibold text-[#0a0a0a]">
        {t("planner.stats.metrics.orders")}
      </h3>
      <div className="grid grid-cols-6 gap-6">
        {/* Zlecenia */}
        <div>
          <div className="text-sm font-semibold text-[#0a0a0a]">
            {t("planner.stats.metrics.orders")}
          </div>
          <div className="mt-1 text-4xl font-bold leading-none text-[#2563eb]">
            {data.ordersCount}
          </div>
          <div className="mt-1 text-base font-medium text-[#737373]">
            {t("planner.stats.summary.kmPerOrder", { value: data.kmPerOrder })}
          </div>
        </div>
        {/* Przychod */}
        <div>
          <div className="text-sm font-semibold text-[#0a0a0a]">
            {t("planner.stats.metrics.revenue")}
          </div>
          <div className="mt-1 text-4xl font-bold leading-none text-[#0a0a0a]">
            {formatNumber(data.revenue)} €
          </div>
          <div className="mt-1 text-base font-medium text-[#737373]">
            {formatDecimal(data.revenuePerKm, 2)} € / km
          </div>
        </div>
        {/* Dystans */}
        <div>
          <div className="text-sm font-semibold text-[#0a0a0a]">
            {t("planner.stats.metrics.distance")}
          </div>
          <div className="mt-1 text-4xl font-bold leading-none text-[#0a0a0a]">
            {formatNumber(data.distance)} km
          </div>
          <div className="mt-1 text-base font-medium text-[#737373]">
            {t("planner.stats.summary.eurPerOrder", {
              value: formatNumber(data.costPerOrder),
            })}
          </div>
        </div>
        {/* Oplaty drogowe */}
        <div>
          <div className="text-sm font-semibold text-[#0a0a0a]">
            {t("planner.stats.metrics.roadCosts")}
          </div>
          <div className="mt-1 text-4xl font-bold leading-none text-[#0a0a0a]">
            {formatNumber(data.roadCosts)} €
          </div>
          <div className="mt-1 text-base font-medium text-[#737373]">
            ~{formatDecimal(data.roadCostsPerKm, 2)} € / 100 km
          </div>
        </div>
        {/* Paliwo */}
        <div>
          <div className="text-sm font-semibold text-[#0a0a0a]">
            {t("planner.stats.metrics.fuel")}
          </div>
          <div className="mt-1 text-4xl font-bold leading-none text-[#0a0a0a]">
            {formatNumber(data.fuel)} l
          </div>
          <div className="mt-1 text-base font-medium text-[#737373]">
            {data.fuelPerKm} l / 100 km
          </div>
        </div>
        {/* Czas jazdy */}
        <div>
          <div className="text-sm font-semibold text-[#0a0a0a]">
            {t("planner.stats.metrics.drivingTime")}
          </div>
          <div className="mt-1 text-4xl font-bold leading-none text-[#0a0a0a]">
            {formatNumber(data.drivingTime)} h
          </div>
          <div className="mt-1 text-base font-medium text-[#737373]">
            {t("planner.stats.summary.hoursPerOrder", {
              value: data.hoursPerOrder,
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// Distance Comparison Card
function DistanceComparisonCard({ data }: { data: DistanceComparison }) {
  const { t } = useTranslation()
  const isPositiveDiff = data.difference > 0
  return (
    <div className="rounded-lg border border-[#e5e5e5] bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-lg font-semibold text-[#0a0a0a]">
        {t("planner.stats.metrics.distance")}
      </h3>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.planned")}
          </div>
          <div className="mt-1 text-2xl font-bold text-[#0a0a0a]">
            {formatNumber(data.planned)} km
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.actual")}
          </div>
          <div className="mt-1 text-2xl font-bold text-[#0a0a0a]">
            {formatNumber(data.actual)} km
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.difference")}
          </div>
          <div
            className={cn(
              "mt-1 text-2xl font-bold",
              isPositiveDiff ? "text-[#dc2626]" : "text-[#15803d]",
            )}
          >
            {isPositiveDiff ? "+" : ""}
            {formatNumber(data.difference)} km
          </div>
          <div
            className={cn(
              "text-sm font-medium",
              isPositiveDiff ? "text-[#dc2626]" : "text-[#15803d]",
            )}
          >
            {isPositiveDiff ? "+" : ""}
            {formatDecimal(data.differencePercent, 2)}%
          </div>
        </div>
      </div>
    </div>
  )
}

// Road Costs Comparison Card
function RoadCostsComparisonCard({ data }: { data: RoadCostsComparison }) {
  const { t } = useTranslation()
  const isPositiveDiff = data.difference > 0
  return (
    <div className="rounded-lg border border-[#e5e5e5] bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-lg font-semibold text-[#0a0a0a]">
        {t("planner.stats.metrics.roadCosts")}
      </h3>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.planned")}
          </div>
          <div className="mt-1 text-2xl font-bold text-[#0a0a0a]">
            {formatNumber(data.planned)} €
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.actual")}
          </div>
          <div className="mt-1 text-2xl font-bold text-[#0a0a0a]">
            {formatNumber(data.actual)} €
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.difference")}
          </div>
          <div
            className={cn(
              "mt-1 text-2xl font-bold",
              isPositiveDiff ? "text-[#dc2626]" : "text-[#15803d]",
            )}
          >
            {isPositiveDiff ? "+" : ""}
            {formatNumber(data.difference)} €
          </div>
          <div
            className={cn(
              "text-sm font-medium",
              isPositiveDiff ? "text-[#dc2626]" : "text-[#15803d]",
            )}
          >
            {isPositiveDiff ? "+" : ""}
            {formatDecimal(data.differencePercent, 2)}%
          </div>
        </div>
      </div>
    </div>
  )
}

// Fuel Comparison Card
function FuelComparisonCard({ data }: { data: FuelComparison }) {
  const { t } = useTranslation()
  const isPositiveDiff = data.difference > 0
  return (
    <div className="rounded-lg border border-[#e5e5e5] bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-lg font-semibold text-[#0a0a0a]">
        {t("planner.stats.metrics.fuel")}
      </h3>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.planned")}
          </div>
          <div className="mt-1 text-2xl font-bold text-[#0a0a0a]">
            {formatNumber(data.planned)} l
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.actual")}
          </div>
          <div className="mt-1 text-2xl font-bold text-[#0a0a0a]">
            {formatNumber(data.actual)} l
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.difference")}
          </div>
          <div
            className={cn(
              "mt-1 text-2xl font-bold",
              isPositiveDiff ? "text-[#dc2626]" : "text-[#15803d]",
            )}
          >
            {isPositiveDiff ? "+" : ""}
            {formatNumber(data.difference)} l
          </div>
          <div
            className={cn(
              "text-sm font-medium",
              isPositiveDiff ? "text-[#dc2626]" : "text-[#15803d]",
            )}
          >
            {isPositiveDiff ? "+" : ""}
            {formatDecimal(data.differencePercent, 2)}%
          </div>
        </div>
      </div>
    </div>
  )
}

// Loaded KM Comparison Card
function LoadedKmComparisonCard({ data }: { data: LoadedKmComparison }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg border border-[#e5e5e5] bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-lg font-semibold text-[#0a0a0a]">
        {t("planner.stats.summary.loadedEmptyKm")}
      </h3>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.totalDistance")}
          </div>
          <div className="mt-1 text-2xl font-bold text-[#0a0a0a]">
            {formatNumber(data.totalDistance)} km
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.loadedKm")}
          </div>
          <div className="mt-1 text-2xl font-bold text-[#15803d]">
            {formatNumber(data.loadedKm)} km
          </div>
          <div className="text-sm font-medium text-[#15803d]">
            {formatDecimal(data.loadedPercent, 1)}%
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-[#737373]">
            {t("planner.stats.summary.emptyKm")}
          </div>
          <div className="mt-1 text-2xl font-bold text-[#dc2626]">
            {formatNumber(data.emptyKm)} km
          </div>
          <div className="text-sm font-medium text-[#dc2626]">
            {formatDecimal(data.emptyPercent, 1)}%
          </div>
        </div>
      </div>
    </div>
  )
}

export function SummaryTab({
  ordersSummary,
  distanceComparison,
  roadCostsComparison,
  fuelComparison,
  loadedKmComparison,
}: SummaryTabProps) {
  return (
    <div className="space-y-4">
      {/* Top row: Orders summary horizontal card - full width */}
      <OrdersSummaryCard data={ordersSummary} />

      {/* Bottom: Four comparison cards stacked vertically - half width */}
      <div className="w-1/2 space-y-4">
        <DistanceComparisonCard data={distanceComparison} />
        <RoadCostsComparisonCard data={roadCostsComparison} />
        <FuelComparisonCard data={fuelComparison} />
        <LoadedKmComparisonCard data={loadedKmComparison} />
      </div>
    </div>
  )
}
